import { inject, Injectable, OnDestroy } from '@angular/core';
import { Observable, of, ReplaySubject, Subscription, switchMap } from 'rxjs';
import { MicrosoftDialPlanExceptionGroup } from '../../shared/models/generated/ziroModels';
import { HttpClient } from '@angular/common/http';
import { ToastService } from '../../shared/services/toast.service';
import { GlobalPropertiesContext } from '../../shared/contexts/global-properties.context';
import { NavigationEnd, Router } from '@angular/router';
import { delayWhen, filter, tap } from 'rxjs/operators';

@Injectable()
export class MsDialPlanExceptionGroupsContext implements OnDestroy {
  _stateSource = new ReplaySubject<MicrosoftDialPlanExceptionGroup[]>(1);
  state$ = this._stateSource.asObservable();

  private http = inject(HttpClient);
  private toastService = inject(ToastService);
  private globalPropertiesContext = inject(GlobalPropertiesContext);
  private _router = inject(Router);

  private _tenantId: number;
  private isHosted: boolean;
  private isWorkbench: boolean;
  private _subscriptions = new Subscription();

  constructor() {
    this._checkCurrentRoute();
    const sub = this._router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((event) => {
        const match = event.urlAfterRedirects.match(/tenants\/(\d+)/);
        if (match) {
          const tenantId = Number(match[1]);
          if (tenantId && tenantId !== this._tenantId) {
            this._setTenantId(tenantId);
          }
        }
      });
    this._subscriptions.add(sub);
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  getDynamicNonTeamsDialPlanExceptionGroups(tenantId: string | number): Observable<MicrosoftDialPlanExceptionGroup[]> {
    return this.http.get<MicrosoftDialPlanExceptionGroup[]>(
      `/services/microsoft/admin/tenants/${tenantId}/dynamic-non-teams-dial-plan-exception-groups`,
    );
  }

  post(body: MicrosoftDialPlanExceptionGroup): Observable<void> {
    return this.http
      .post<void>(`/services/microsoft/admin/tenants/${this._tenantId}/dial-plan-exception-groups`, body)
      .pipe(delayWhen(() => this.getAll()));
  }

  put(body: MicrosoftDialPlanExceptionGroup): Observable<void> {
    return this.http
      .put<void>(`/services/microsoft/admin/tenants/${this._tenantId}/dial-plan-exception-groups/${body.id}`, body)
      .pipe(delayWhen(() => this.getAll()));
  }

  delete(id: number, name: string): Observable<void> {
    return this.http
      .delete<void>(`/services/microsoft/admin/tenants/${this._tenantId}/dial-plan-exception-groups/${id}`)
      .pipe(
        delayWhen(() => this.getAll()),
        tap(() => {
          this.toastService.pushDeleteToast('tkey;admin.ms_dial_plan_exception.toast.title', name);
        }),
      );
  }

  getAll(tenantId = this._tenantId, isHosted = this.isHosted): Observable<MicrosoftDialPlanExceptionGroup[]> {
    this.isWorkbench = window.location.href.includes('workbench');
    if (!isHosted && !this.isWorkbench) {
      this._stateSource.next(null);
      return of(null);
    }

    return this.http
      .get<
        MicrosoftDialPlanExceptionGroup[]
      >(`/services/microsoft/admin/tenants/${tenantId}/dial-plan-exception-groups`)
      .pipe(
        tap((data: MicrosoftDialPlanExceptionGroup[]) => {
          this._stateSource.next(data);
        }),
      );
  }

  private _checkCurrentRoute(): void {
    const currentUrl = this._router.url;
    this._extractTenantFromUrl(currentUrl);
  }

  private _extractTenantFromUrl(url: string): void {
    const match = url.match(/tenants\/(\d+)/);
    if (match) {
      const tenantId = Number(match[1]);
      if (tenantId && tenantId !== this._tenantId) {
        this._setTenantId(tenantId);
      }
    }
  }

  private _setTenantId(tenantId: number): void {
    this._tenantId = tenantId;

    const sub = this.globalPropertiesContext.state$
      .pipe(
        switchMap((globalProperties) => {
          this.isHosted = globalProperties.hostedEnabled;
          this.isWorkbench = window.location.href.includes('workbench');
          return this.getAll();
        }),
      )
      .subscribe();

    this._subscriptions.add(sub);
  }
}
